import React, { useEffect, useRef, useState } from "react";

export default function BoardSelector({
  boards,
  selectedBoardId,
  onChange,
  onCreateBoard,
  onRenameBoard,
  onDeleteBoard,
}) {
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editName, setEditName] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const selectedBoard = boards.find((b) => b.id === selectedBoardId);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    // click, не mousedown — иначе тот же жест может сразу закрыть меню
    document.addEventListener("click", onDoc);
    window.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("click", onDoc);
      window.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const board = await onCreateBoard({ name: newName.trim() });
      setNewName("");
      setShowForm(false);
      onChange(board.id);
    } finally {
      setCreating(false);
    }
  };

  const handleStartRename = () => {
    setMenuOpen(false);
    setShowForm(false);
    setEditName(selectedBoard?.name || "");
    setEditing(true);
  };

  const handleRename = async (e) => {
    e.preventDefault();
    if (!editName.trim() || !selectedBoardId) return;
    setSaving(true);
    try {
      await onRenameBoard(selectedBoardId, { name: editName.trim() });
      setEditing(false);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedBoard || selectedBoard.is_default) return;
    setMenuOpen(false);
    const count = selectedBoard.task_count || 0;
    const warning =
      count > 0
        ? `Доска «${selectedBoard.name}» содержит задач: ${count}. Они будут удалены безвозвратно вместе с доской.`
        : `Удалить доску «${selectedBoard.name}»?`;
    if (!window.confirm(warning)) return;
    setDeleting(true);
    try {
      await onDeleteBoard(selectedBoardId);
      setEditing(false);
    } finally {
      setDeleting(false);
    }
  };

  const handleStartCreate = () => {
    setMenuOpen(false);
    setEditing(false);
    setShowForm(true);
  };

  return (
    <div className="board-selector">
      <label className="task-filter-field board-selector-field">
        <span className="task-filter-label">Доска</span>
        <select
          className="task-form-select board-selector-select"
          value={selectedBoardId || ""}
          onChange={(e) => onChange(Number(e.target.value))}
        >
          {boards.map((board) => (
            <option key={board.id} value={board.id}>
              {board.name}
              {board.is_default ? " · по умолчанию" : ""}
              {board.task_count != null ? ` · ${board.task_count}` : ""}
            </option>
          ))}
        </select>
      </label>

      <div className="board-selector-more" ref={menuRef}>
        <button
          type="button"
          className="btn btn-sm board-selector-menu-btn"
          aria-expanded={menuOpen}
          aria-haspopup="menu"
          aria-label="Управление доской"
          title="Управление доской"
          onClick={(e) => {
            e.stopPropagation();
            setMenuOpen((v) => !v);
          }}
        >
          ⋯
        </button>
        {menuOpen && (
          <div className="board-selector-menu" role="menu">
            <button type="button" role="menuitem" onClick={handleStartRename}>
              Переименовать
            </button>
            <button type="button" role="menuitem" onClick={handleStartCreate}>
              + Новая доска
            </button>
            {selectedBoard && !selectedBoard.is_default && (
              <button
                type="button"
                role="menuitem"
                className="board-selector-menu-danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? "Удаление…" : "Удалить доску"}
              </button>
            )}
          </div>
        )}
      </div>

      {editing && (
        <form className="board-create-form" onSubmit={handleRename}>
          <input
            className="task-form-input"
            placeholder="Новое название доски"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            maxLength={200}
            autoFocus
          />
          <button type="submit" className="btn btn-primary btn-sm" disabled={saving}>
            {saving ? "Сохранение…" : "Сохранить"}
          </button>
          <button type="button" className="btn btn-sm" onClick={() => setEditing(false)}>
            Отмена
          </button>
        </form>
      )}

      {showForm && (
        <form className="board-create-form" onSubmit={handleCreate}>
          <input
            className="task-form-input"
            placeholder="Название новой доски"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            maxLength={200}
            autoFocus
          />
          <button type="submit" className="btn btn-primary btn-sm" disabled={creating}>
            {creating ? "Создание…" : "Создать"}
          </button>
          <button type="button" className="btn btn-sm" onClick={() => setShowForm(false)}>
            Отмена
          </button>
        </form>
      )}
    </div>
  );
}
